import moment from 'moment'
import {Event} from '../lib/constants/Event'

/** @ignore */
const HST_KEY = '__history__',
    HST_MAX_LENGTH = 15,
    HST_VERSION = 3,
    MIN_ARTICLE_DATA_CHUNK_SIZE_TO_KEEP = 100

/**
 * History api class handles local changes to a document and allows access
 * to a limited versioning of local changes.
 *
 * Changes are saved periodically to local store. If the change should create a new version, these statements should
 * be fulfilled:
 * <ul>
 *     <li> The edit operation removes text </li>
 *     <li> The removed text is of a certain size, smaller edits do not count for new version</li>
 * </ul>
 *
 * A maximum number of 15 versions are stored per article.
 *
 * @class
 * @alias api.history
 * @private
 */
class History {

    /** @hideconstructor */
    constructor(api) {
        this.api = api
        this.storage = null
    }

    /**
     * Check that local storage is available before any operation is performed
     *
     * @returns {boolean}
     */
    isAvailable() {
        if (this.storage !== null) {
            return (this.storage !== false)
        }

        try {
            const storage = window['localStorage']
            const x = '__storage_test__'

            storage.setItem(x, x)
            storage.removeItem(x)
            this.storage = storage

            return true
        }
        catch (e) {
            this.storage = false
            return false
        }
    }


    /**
     * Get a history object for uuid
     * @returns {*}
     * @param id
     */
    get(id) {
        if (!this.isAvailable()) {
            return false
        }

        let doc = this.getHistoryForArticle(id)
        return doc || this.createAndAddEmptyHistory(id)
    }


    createAndAddEmptyHistory(id) {

        let articleHistory = []
        if (!this.isAvailable()) {
            return false
        }
        let history = {id: id, versions: [], updated: moment().format()}

        // If this is temporary, add a property to keep track
        if (this.api.newsItem.hasTemporaryId()) {
            history['unsavedArticle'] = true
        }

        articleHistory.push(history)

        return history
    }


    /**
     * Deletes history for an ID
     * @param id
     */
    deleteHistory(id) {

        if (!this.isAvailable()) {
            return false
        }

        const historyId = HST_KEY + id
        this.storage.removeItem(historyId)
        this.api.events.trigger(null, Event.HISTORY_CLEARED, {historyId: historyId})
    }

    deleteAll() {
        Object.keys(this.storage).filter((key) => {
            return key.indexOf('__history__') >= 0
        }).map((historyKey) => {
            return this.storage[historyKey]
        }).forEach((article) => {
            const parsedArticle = JSON.parse(article)
            const historyId = HST_KEY + parsedArticle.id
            this.storage.removeItem(historyId)
        })

        this.api.events.trigger(null, Event.HISTORY_CLEARED, {history: 'all'})
    }

    /**
     * Get history from local storage for all articles and JSON Parse to Object
     * @returns {Array}
     */
    getHistory() {
        if (!this.isAvailable()) {
            return false
        }

        const history = Object.keys(this.storage).filter((key) => {
            return key.indexOf('__history__') >= 0
        }).map((historyKey) => {
            return {article: this.storage[historyKey], key: historyKey}
        }).map(({article, key}) => {
            try {
                const parsedArticle = JSON.parse(article)
                if (key === '__history__') {
                    this.deleteHistory('')
                    return null
                } else if (!parsedArticle.version && parsedArticle.version !== 3) {
                    this.deleteHistory(parsedArticle.id)
                    return null
                }
                return parsedArticle
            } catch (e) {
                this.api.ui.showNotification('history', null, 'Error loading history')
                return null
            }

        }).filter((article) => {
            return article !== null
        }).sort(function (a, b) {
            let dateA = moment(a.updated),
                dateB = moment(b.updated)

            if (dateA.isBefore(dateB)) {
                return 1
            } else {
                return -1
            }
        })
        return (!history) ? [] : history
    }

    getHistoryForArticle(id) {
        if (!this.isAvailable()) {
            return false
        }

        try {
            let articleHistory = this.storage.getItem(HST_KEY + id)
            return (!articleHistory) ? null : JSON.parse(articleHistory)
        }
        catch (e) {
            this.api.ui.showNotification('history', null, 'Error loading history')
        }
    }

    /**
     * Clears items older than @see HST_MAX_DAYS days.
     */
    cleanVersionsOlderThanMaxDays(days) {
        if (!this.isAvailable()) {
            return false;
        }

        const weekAgo = moment().subtract(days, 'days')

        const itemsToDelete = Object.keys(this.storage)
            .filter((key) => {
                return key.indexOf('__history__') >= 0
            })
            .map((key) => {
                return this.storage[key]
            })
            .filter((articleData) => {
                try {
                    const article = JSON.parse(articleData);
                    return moment(article.updated).isBefore(weekAgo)
                } catch (e) {
                    // Something did not go well, remove the article.
                    return true;
                }
            })

        itemsToDelete.forEach((article) => {
            try {
                const parsedArticle = JSON.parse(article)
                this.deleteHistory(parsedArticle.id)
            } catch (e) {
                console.log('Could not delete article', parsedArticle ? parsedArticle.id : 'unparseableArticle')
            }
        })
    }

    /**
     * Removed half the list of articles based on the updated property
     * @returns {boolean}
     */
    cleanOldVersions() {
        if (!this.isAvailable()) {
            return false
        }

        let articles = Object.keys(this.storage)
            .filter((key) => {
                return key.indexOf('__history__') >= 0
            })
            .map((key) => {
                return this.storage[key]
            })
            .sort(function (a, b) {
                const dateA = moment(a.updated)
                const dateB = moment(b.updated)

                if (dateA.isBefore(dateB)) {
                    return -1
                } else {
                    return 1
                }
            })

        const itemsToDelete = articles.splice(0, Object.keys(this.storage).length / 2)

        itemsToDelete.forEach((article) => {
            try {
                const parsedArticle = JSON.parse(article)
                this.deleteHistory(parsedArticle.id)
            } catch (e) {

            }

        })
    }


    /**
     * Save history to local storage
     * @param id
     * @param history
     * @returns {boolean}
     */
    saveHistory(id, history) {
        if (!this.isAvailable()) {
            return false
        }

        try {
            let historyJson = JSON.stringify(history)
            this.storage.setItem(HST_KEY + id, historyJson)

            const eventData = {
                type: 'add',
                action: 'add',
                data: null
            }
            this.api.events.trigger(null, Event.HISTORY_SAVED, eventData)
        }
        catch (e) {
            throw e
        }
    }

    snapshot(action) {
        if (!this.isAvailable()) {
            return false
        }

        let id = this.api.newsItem.getIdForArticle()

        let doc = this.get(id)
        doc.version = HST_VERSION

        let etag = this.api.router.getEtag(id)
        if (etag) {
            doc.etag = etag
        }

        // Don't grow the version list too much
        if (doc.versions.length >= HST_MAX_LENGTH) {
            doc.versions.shift()
        }

        /*
         * Only save save version
         */
        try {
            const documentSource = this.api.newsItem.getSource()
            doc.updated = moment().format()

            const currentLength = documentSource.length
            const previousHistoryLength = doc.versions[doc.versions.length - 1] ? doc.versions[doc.versions.length - 1].src.length : undefined

            const historyItem = {
                time: moment().format(),
                src: documentSource,
                action: action || null
            }

            if (!previousHistoryLength || History._shouldCreateNewVersion(currentLength, previousHistoryLength)) {
                doc.versions.push(historyItem)
            } else {
                doc.versions[doc.versions.length - 1] = historyItem
            }

        } catch (e) {
            console.error(e)
            console.error('Failed to get document source when saving to local storage')
            return
        }

        try {
            this.saveHistory(id, doc)
        } catch (e) {
            // Handle this clear and retry to handle keys in local storage instead of all articles in one key
            if (e instanceof window.DOMException) {
                this.cleanAndRetry()
            } else {
                this.api.ui.showNotification('history', null, e.message)
            }
        }

    }

    cleanAndRetry() {
        try {
            this.cleanOldVersions()
        } catch (e) {
            this.api.ui.showNotification('history', null, 'Error saving version to local storage')
        }
        finally {
            this.api.ui.showNotification('history', null, 'Error')
        }

    }

    /**
     * Returns false if previous length of article is less than 20
     * or if current length is greater than previous length or
     * if previous length minus current length is smaller than
     * MIN_ARTICLE_DATA_CHUNK_SIZE_TO_KEEP.
     *
     * @param currentLength
     * @param previousLength
     * @return {boolean}
     * @private
     */
    static _shouldCreateNewVersion(currentLength, previousLength) {

        // Ignore small texts
        if (previousLength < 20) {
            return false
        }

        // Text that gets bigger should update existing
        if (currentLength > previousLength) {
            return false
        }

        // Ignore smaller removals
        if (previousLength - currentLength < MIN_ARTICLE_DATA_CHUNK_SIZE_TO_KEEP) {
            return false
        }

        return true
    }

}

export {History}
